import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { track } from "../lib/analytics";

const SCROLL_KEY = "gfirem.homeScrollY";

/**
 * Global scroll behavior for the SPA:
 * - On any route change, scroll to top of the new page.
 * - If the URL has a hash (e.g. /#work), scroll to that section.
 * - Before leaving Home, remember the current window.scrollY so that when the
 *   user comes back to Home via the case-study back link (`/#work`), we can
 *   restore the exact scroll position rather than jumping to the section top.
 */
export default function ScrollToTop() {
    const { pathname, hash } = useLocation();
    const prevPathnameRef = useRef(pathname);

    useEffect(() => {
        const prevPath = prevPathnameRef.current;

        // SPA page_viewed event — fires on every route change (initial render included).
        track("page_viewed", { path: pathname, hash: hash || "" });

        // If we're leaving Home ("/") for any other route, snapshot scrollY.
        if (prevPath === "/" && pathname !== "/") {
            try {
                sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
            } catch (e) {
                /* storage disabled — silently continue */
            }
        }
        prevPathnameRef.current = pathname;

        // Returning to Home with #work hash — try to restore the snapshot first.
        if (pathname === "/" && hash === "#work") {
            let restored = null;
            try {
                restored = sessionStorage.getItem(SCROLL_KEY);
                sessionStorage.removeItem(SCROLL_KEY);
            } catch (e) {
                restored = null;
            }
            if (restored !== null && !Number.isNaN(Number(restored))) {
                // Wait one microtask for the DOM to settle, then jump exactly.
                requestAnimationFrame(() => {
                    window.scrollTo({ top: Number(restored), left: 0, behavior: "instant" });
                });
                return;
            }
        }

        if (hash) {
            const id = hash.replace("#", "");
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: "instant", block: "start" });
                return;
            }
        }
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [pathname, hash]);

    return null;
}
